function getCardQuickAddVariants(modal) {
  const variantsNode = modal.querySelector('[data-card-quick-add-variants]');
  if (!variantsNode) return [];

  try {
    return JSON.parse(variantsNode.textContent);
  } catch (error) {
    console.error('Unable to parse quick add variants', error);
    return [];
  }
}

const cardProductMediaItemsCache = new WeakMap();
const cardInlineQuickAddVariantsCache = new WeakMap();
const initializedCardInlineQuickAdds = new WeakSet();
const initializedCardProductMedia = new WeakSet();
let cardInlineQuickAddObserver = null;
let cardProductMediaObserver = null;
const cardProductFinePointerQuery = window.matchMedia?.('(hover: hover) and (pointer: fine)');
const prefetchedCardQuickAddUrls =
  globalThis.__lyoraPrefetchedCardQuickAddUrls || (globalThis.__lyoraPrefetchedCardQuickAddUrls = new Set());

function supportsCardProductHoverPreview() {
  return !window.matchMedia || Boolean(cardProductFinePointerQuery?.matches);
}

function getCardProductObserverRootMargin(type = 'inline') {
  const isCoarsePointer = window.matchMedia?.('(pointer: coarse)').matches;
  if (type === 'media') return isCoarsePointer ? '650px 0px' : '1200px 0px';
  return isCoarsePointer ? '420px 0px' : '900px 0px';
}

function getCardQuickAddSelectedValues(modal) {
  return Array.from(modal.querySelectorAll('[data-option-position]')).map((group) => {
    const checkedInput = group.querySelector('.card-quick-add-modal__option-input:checked');
    return checkedInput ? checkedInput.value : null;
  });
}

function getOptionLabelForInput(root, input) {
  if (!root || !input?.id) return null;

  const escapedId = window.CSS?.escape ? window.CSS.escape(input.id) : input.id.replace(/"/g, '\\"');
  return root.querySelector(`label[for="${escapedId}"]`);
}

function normalizeCardProductMediaItem(item) {
  if (!item || !item.s) return item;

  const src = item.s;
  const width = Number(item.w) || 0;
  const srcsetParts = [];
  if (width >= 360) srcsetParts.push(`${src.replace(/([?&]width=)\d+/, '$1360')} 360w`);
  srcsetParts.push(width >= 720 || !width ? `${src} 720w` : `${src} ${width}w`);

  return {
    id: item.id,
    src,
    srcset: srcsetParts.join(', '),
    alt: item.a,
    width: item.w,
    height: item.h,
  };
}

function parseCardProductMediaItems(json) {
  const items = JSON.parse(json);
  return Array.isArray(items) ? items.map(normalizeCardProductMediaItem) : [];
}

function getCardProductMediaItems(cardWrapper) {
  if (cardProductMediaItemsCache.has(cardWrapper)) return cardProductMediaItemsCache.get(cardWrapper);

  const mediaItemsData = cardWrapper.dataset.cardProductMediaItems;
  if (mediaItemsData) {
    try {
      const mediaItems = parseCardProductMediaItems(mediaItemsData);
      cardProductMediaItemsCache.set(cardWrapper, mediaItems);
      return mediaItems;
    } catch (error) {
      console.error('Unable to parse product card media items from data attribute', error);
    }
  }

  const mediaItemsNode = cardWrapper.querySelector('[data-card-product-media-items]');
  if (!mediaItemsNode) return [];

  try {
    const mediaItems = parseCardProductMediaItems(mediaItemsNode.textContent);
    cardProductMediaItemsCache.set(cardWrapper, mediaItems);
    return mediaItems;
  } catch (error) {
    console.error('Unable to parse product card media items', error);
    return [];
  }
}

function setActiveCardSwatch(cardWrapper, activeButton) {
  if (!cardWrapper) return;

  cardWrapper.querySelectorAll('.card-swatches__list .card-swatch-button').forEach((button) => {
    if (button === activeButton) {
      button.setAttribute('aria-current', 'true');
    } else {
      button.removeAttribute('aria-current');
    }
  });
}

function getCardProductMediaIndex(cardWrapper) {
  const index = Number(cardWrapper.dataset.cardProductMediaIndex || 0);
  return Number.isNaN(index) ? 0 : index;
}

function syncCardProductMediaButtons(cardWrapper) {
  const mediaItems = getCardProductMediaItems(cardWrapper);
  const hasMultipleMedia = mediaItems.length > 1;

  cardWrapper.querySelectorAll('[data-card-product-media-nav]').forEach((button) => {
    button.hidden = !hasMultipleMedia;
    button.disabled = !hasMultipleMedia;
  });
}

function setCardProductMediaFromItem(cardWrapper, mediaItem, mediaIndex = 0) {
  if (!mediaItem) return;

  const mainImage = cardWrapper.querySelector('[data-card-product-main-image]');
  if (!mainImage) return;

  mainImage.src = mediaItem.src;
  if (mediaItem.srcset) mainImage.srcset = mediaItem.srcset;
  else mainImage.srcset = mediaItem.src;
  if (mediaItem.alt !== undefined) mainImage.alt = mediaItem.alt;
  if (mediaItem.width) mainImage.width = mediaItem.width;
  if (mediaItem.height) mainImage.height = mediaItem.height;

  cardWrapper.dataset.cardProductMediaIndex = String(mediaIndex);
}

function setCardProductMediaByIndex(cardWrapper, index) {
  const mediaItems = getCardProductMediaItems(cardWrapper);
  if (!mediaItems.length) return;

  const normalizedIndex = ((index % mediaItems.length) + mediaItems.length) % mediaItems.length;
  setCardProductMediaFromItem(cardWrapper, mediaItems[normalizedIndex], normalizedIndex);
}

function setCardProductMediaById(cardWrapper, mediaId) {
  if (!mediaId) return false;

  const mediaItems = getCardProductMediaItems(cardWrapper);
  const mediaIndex = mediaItems.findIndex((item) => String(item.id) === String(mediaId));
  if (mediaIndex === -1) return false;

  setCardProductMediaFromItem(cardWrapper, mediaItems[mediaIndex], mediaIndex);
  return true;
}

function setCardProductMediaBySource(cardWrapper, source) {
  if (!source) return false;

  const mediaItems = getCardProductMediaItems(cardWrapper);
  const mediaIndex = mediaItems.findIndex((item) => item.src === source);
  if (mediaIndex === -1) return false;

  setCardProductMediaFromItem(cardWrapper, mediaItems[mediaIndex], mediaIndex);
  return true;
}

function commitCardProductCurrentImage(cardWrapper) {
  if (!cardWrapper) return;
  cardWrapper.dataset.cardProductCommittedMediaIndex = String(getCardProductMediaIndex(cardWrapper));
}

function getCardInlineQuickAddMatchedVariant(root, selectedValues) {
  const variants = getCardInlineQuickAddVariants(root);
  if (!variants.length) return null;

  return (
    variants.find(
      (variant) =>
        variant.available &&
        variant.featured_media?.src &&
        variant.options.every((optionValue, index) => optionValue === selectedValues[index])
    ) ||
    variants.find((variant) => variant.options.every((optionValue, index) => optionValue === selectedValues[index])) ||
    null
  );
}

function updateCardQuickAddPrice(modal, variant) {
  const priceContainer = modal.querySelector('[data-card-quick-add-price]');
  if (!priceContainer || !variant) return;

  const currencyCodeEnabled = modal.dataset.currencyCodeEnabled === 'true';
  const priceText = currencyCodeEnabled
    ? variant.price_with_currency_formatted
    : variant.price_formatted;
  const compareText = currencyCodeEnabled
    ? variant.compare_at_price_with_currency_formatted
    : variant.compare_at_price_formatted;

  const compareMarkup =
    compareText && compareText !== priceText
      ? `<span class="compare-price-wrap"><s class="card-quick-add-modal__price-compare compare-price">${compareText}</s></span>`
      : '';
  priceContainer.innerHTML = `<div class="price-container"><span class="card-quick-add-modal__price-current current-price">${priceText}</span>${compareMarkup}</div>`;

  const buttonPriceCurrent = modal.querySelector('[data-button-price-current]');
  const buttonPriceCompare = modal.querySelector('[data-button-price-compare]');
  const buttonPriceWrap = modal.querySelector('[data-button-price-wrap]');
  if (buttonPriceCurrent) buttonPriceCurrent.textContent = priceText;
  if (buttonPriceCompare) {
    buttonPriceCompare.textContent = compareText || '';
    buttonPriceCompare.hidden = !(compareText && compareText !== priceText);
  }
  if (buttonPriceWrap) buttonPriceWrap.hidden = !variant.available;
}

function getCardQuickAddMediaScrollAmount(track) {
  const firstItem = track?.querySelector('.card-quick-add-modal__media-item');
  if (!track || !firstItem) return 0;

  const gap = parseFloat(window.getComputedStyle(track).gap || '0');
  return firstItem.getBoundingClientRect().width + gap;
}

function updateCardQuickAddDots(modal) {
  const track = modal.querySelector('.card-quick-add-modal__media-track');
  const dots = modal.querySelectorAll('[data-card-quick-add-media-dot]');
  const scrollAmount = getCardQuickAddMediaScrollAmount(track);
  if (!track || !dots.length || !scrollAmount) return;

  const activeIndex = Math.round(track.scrollLeft / scrollAmount);
  dots.forEach((dot, index) => {
    dot.classList.toggle('is-active', index === activeIndex);
  });
}

function scrollCardQuickAddMediaToIndex(modal, index = 0) {
  const track = modal.querySelector('.card-quick-add-modal__media-track');
  const scrollAmount = getCardQuickAddMediaScrollAmount(track);
  if (!track || !scrollAmount) return;

  track.scrollTo({ left: index * scrollAmount, behavior: 'smooth' });
}

function scrollCardQuickAddMediaByDirection(modal, direction = 'next') {
  const track = modal.querySelector('.card-quick-add-modal__media-track');
  const scrollAmount = getCardQuickAddMediaScrollAmount(track);
  if (!track || !scrollAmount) return;

  const currentIndex = Math.round(track.scrollLeft / scrollAmount);
  const itemCount = track.querySelectorAll('.card-quick-add-modal__media-item').length;
  if (!itemCount) return;

  const nextIndex =
    direction === 'prev'
      ? (currentIndex - 1 + itemCount) % itemCount
      : (currentIndex + 1) % itemCount;

  scrollCardQuickAddMediaToIndex(modal, nextIndex);
}

function scrollCardQuickAddMediaToId(modal, mediaId) {
  if (!mediaId) return;

  const items = Array.from(modal.querySelectorAll('.card-quick-add-modal__media-item'));
  const mediaIndex = items.findIndex((item) => String(item.dataset.cardQuickAddMediaId) === String(mediaId));
  if (mediaIndex === -1) return;

  scrollCardQuickAddMediaToIndex(modal, mediaIndex);
}

function getMatchedCardVariant(variants, selectedValues) {
  if (!variants.length) return null;

  const hasExplicitSelections = selectedValues.some(
    (value) => value !== null && value !== undefined && value !== ''
  );

  if (!selectedValues.length || !hasExplicitSelections) {
    return variants.find((variant) => variant.available) || variants[0];
  }

  return (
    variants.find((variant) => variant.options.every((optionValue, index) => optionValue === selectedValues[index])) ||
    null
  );
}

function updateCardQuickAddState(modal) {
  const variants = getCardQuickAddVariants(modal);
  if (!variants.length) return;

  const selectedValues = getCardQuickAddSelectedValues(modal);
  const matchedVariant = getMatchedCardVariant(variants, selectedValues);

  const variantInput = modal.querySelector('.product-variant-id');
  const quantityInput = modal.querySelector('.product-quantity-value');
  const submitButton = modal.querySelector('.card-quick-add-modal__submit');
  const statusMessage = modal.querySelector('[data-card-quick-add-status]');
  const submitLabel =
    submitButton?.querySelector('[data-submit-text]') ||
    submitButton?.querySelector('span:not(.sold-out-message):not(.loading__spinner)');

  if (!matchedVariant || !variantInput || !submitButton) {
    if (variantInput) variantInput.value = '';
    if (submitButton) {
      submitButton.setAttribute('disabled', 'disabled');
      submitButton.setAttribute('aria-disabled', 'true');
    }
    modal.querySelector('[data-button-price-wrap]')?.setAttribute('hidden', 'hidden');
    if (statusMessage) statusMessage.hidden = false;
    return;
  }

  variantInput.value = matchedVariant.id;
  variantInput.disabled = !matchedVariant.available;
  if (quantityInput) quantityInput.value = matchedVariant.quantity_rule_min || 1;

  updateCardQuickAddPrice(modal, matchedVariant);
  scrollCardQuickAddMediaToId(modal, matchedVariant.featured_media_id);

  if (matchedVariant.available) {
    submitButton.removeAttribute('disabled');
    submitButton.removeAttribute('aria-disabled');
    modal.querySelector('[data-button-price-wrap]')?.removeAttribute('hidden');
    if (statusMessage) statusMessage.hidden = true;
    if (submitLabel) submitLabel.textContent = window.variantStrings?.addToCart || 'Add to cart';
  } else {
    submitButton.setAttribute('disabled', 'disabled');
    submitButton.setAttribute('aria-disabled', 'true');
    modal.querySelector('[data-button-price-wrap]')?.setAttribute('hidden', 'hidden');
    if (statusMessage) statusMessage.hidden = false;
    if (submitLabel) submitLabel.textContent = window.variantStrings?.soldOut || 'Sold out';
  }

  modal.querySelectorAll('[data-option-position]').forEach((group, groupIndex) => {
    const selectedLabel = group.querySelector('[data-selected-value]');
    if (selectedLabel) selectedLabel.textContent = selectedValues[groupIndex] || '';

    group.querySelectorAll('.card-quick-add-modal__option-input').forEach((input) => {
      const testValues = [...selectedValues];
      testValues[groupIndex] = input.value;

      const hasMatch = variants.some(
        (variant) =>
          variant.available &&
          variant.options.every((optionValue, index) => optionValue === testValues[index])
      );

      input.disabled = !hasMatch;
      const chip = getOptionLabelForInput(modal, input);
      if (chip) chip.classList.toggle('is-unavailable', !hasMatch);
    });
  });
}

function getCardInlineQuickAddVariants(root) {
  if (cardInlineQuickAddVariantsCache.has(root)) return cardInlineQuickAddVariantsCache.get(root);

  const variantsNode = root.querySelector('[data-card-inline-quick-add-variants]');
  if (!variantsNode) return [];

  try {
    const variants = JSON.parse(variantsNode.textContent);
    cardInlineQuickAddVariantsCache.set(root, variants);
    return variants;
  } catch (error) {
    console.error('Unable to parse inline quick add variants', error);
    return [];
  }
}

function getCardInlineQuickAddOptionGroups(root) {
  return Array.from(root.querySelectorAll('[data-card-inline-quick-add-option-position]')).sort(
    (leftGroup, rightGroup) =>
      Number(leftGroup.dataset.cardInlineQuickAddOptionPosition || 0) -
      Number(rightGroup.dataset.cardInlineQuickAddOptionPosition || 0)
  );
}

function getCardInlineQuickAddSelectedValues(root) {
  return getCardInlineQuickAddOptionGroups(root).map((group) => {
    const checkedInput = group.querySelector('.card-inline-quick-add__option-input:checked');
    return checkedInput ? checkedInput.value : null;
  });
}

function updateCardInlineQuickAddSelectedLabels(root, selectedValues = getCardInlineQuickAddSelectedValues(root)) {
  getCardInlineQuickAddOptionGroups(root).forEach((group, groupIndex) => {
    const selectedLabel = root.querySelector(
      `[data-card-inline-quick-add-selected-value="${group.dataset.cardInlineQuickAddOptionPosition}"]`
    );
    if (selectedLabel) selectedLabel.textContent = selectedValues[groupIndex] || '';
  });
}

function setCardInlineQuickAddConfirmLabel(confirmButton, label) {
  const labelNode = confirmButton?.querySelector('[data-card-inline-quick-add-confirm-label]');
  if (!labelNode || !label) return;
  labelNode.textContent = label;
}

function findCardInlineQuickAddOptionInput(root, optionPosition, value) {
  const group = Array.from(root.querySelectorAll('[data-card-inline-quick-add-option-position]')).find(
    (optionGroup) => optionGroup.dataset.cardInlineQuickAddOptionPosition === String(optionPosition)
  );

  if (!group) return null;

  return Array.from(group.querySelectorAll('.card-inline-quick-add__option-input')).find((input) => input.value === value) || null;
}

function setCardInlineQuickAddExpanded(root, expanded) {
  root.classList.toggle('is-expanded', expanded);

  const toggle = root.querySelector('[data-card-inline-quick-add-toggle]');
  if (toggle) toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
}

function closeAllCardInlineQuickAdds(exceptRoot = null) {
  document.querySelectorAll('[data-card-inline-quick-add].is-expanded').forEach((root) => {
    if (root !== exceptRoot) setCardInlineQuickAddExpanded(root, false);
  });
}

function updateCardInlineQuickAddPrice(cardWrapper, variant, currencyCodeEnabled) {
  const priceContainer = cardWrapper.querySelector('.price');
  if (!priceContainer || !variant) return;

  const priceText = currencyCodeEnabled
    ? variant.price_with_currency_formatted
    : variant.price_formatted;
  const compareText = currencyCodeEnabled
    ? variant.compare_at_price_with_currency_formatted
    : variant.compare_at_price_formatted;

  const regularPrice = priceContainer.querySelector('.price__regular .price-item--regular');
  const comparePrice = priceContainer.querySelector('.price__sale .price-item--regular');
  const salePrice = priceContainer.querySelector('.price__sale .price-item--sale');

  if (regularPrice) regularPrice.textContent = priceText;
  if (comparePrice) comparePrice.textContent = compareText || '';
  if (salePrice) salePrice.textContent = priceText;

  priceContainer.classList.toggle('price--on-sale', Boolean(compareText && compareText !== priceText));
}

function updateCardInlineQuickAddImage(cardWrapper, variant) {
  const featuredMedia = variant?.featured_media;
  if (!featuredMedia?.src) return;

  if (setCardProductMediaById(cardWrapper, featuredMedia.id)) return;
  if (setCardProductMediaBySource(cardWrapper, featuredMedia.src)) return;

  const mainImage = cardWrapper.querySelector('[data-card-product-main-image]');
  if (!mainImage) return;

  mainImage.src = featuredMedia.src;
  if (featuredMedia.srcset) mainImage.srcset = featuredMedia.srcset;
  if (featuredMedia.alt !== undefined) mainImage.alt = featuredMedia.alt;
  if (featuredMedia.width) mainImage.width = featuredMedia.width;
  if (featuredMedia.height) mainImage.height = featuredMedia.height;
}

function updateCardProductImageFromSwatch(cardWrapper, swatchButton) {
  const mediaId = swatchButton.dataset.cardSwatchMediaId;
  if (mediaId && setCardProductMediaById(cardWrapper, mediaId)) return;

  const src = swatchButton.dataset.cardSwatchMediaSrc;
  if (!src) return;

  if (setCardProductMediaBySource(cardWrapper, src)) return;

  const mainImage = cardWrapper.querySelector('[data-card-product-main-image]');
  if (!mainImage) return;

  mainImage.src = src;
  if (swatchButton.dataset.cardSwatchMediaSrcset) mainImage.srcset = swatchButton.dataset.cardSwatchMediaSrcset;
  if (swatchButton.dataset.cardSwatchMediaAlt !== undefined) mainImage.alt = swatchButton.dataset.cardSwatchMediaAlt;
  if (swatchButton.dataset.cardSwatchMediaWidth) mainImage.width = swatchButton.dataset.cardSwatchMediaWidth;
  if (swatchButton.dataset.cardSwatchMediaHeight) mainImage.height = swatchButton.dataset.cardSwatchMediaHeight;
}

function syncCardInlineQuickAddSwatches(root) {
  const cardWrapper = root.closest('.card-wrapper');
  if (!cardWrapper) return;

  cardWrapper.querySelectorAll('.card-swatch-button[data-card-swatch-option-position]').forEach((button) => {
    const input = findCardInlineQuickAddOptionInput(
      root,
      button.dataset.cardSwatchOptionPosition,
      button.dataset.cardSwatchValue || button.dataset.value || ''
    );

    if (!input) return;

    if (input.checked) {
      button.setAttribute('aria-current', 'true');
    } else {
      button.removeAttribute('aria-current');
    }

    button.classList.toggle('is-unavailable', input.disabled);
    button.disabled = input.disabled;
  });
}

function submitCardInlineQuickAdd(root) {
  const productForm = root.querySelector('product-form');
  const form = productForm?.form || productForm?.querySelector('form');
  const submitButton = root.querySelector('.card-inline-quick-add__submit');
  const confirmButton = root.querySelector('[data-card-inline-quick-add-confirm]');

  if (!form || !submitButton || submitButton.disabled || submitButton.getAttribute('aria-disabled') === 'true') return;

  if (confirmButton) {
    confirmButton.classList.remove('is-added');
    confirmButton.classList.add('loading', 'is-clicked');
    confirmButton.setAttribute('aria-disabled', 'true');
    setCardInlineQuickAddConfirmLabel(confirmButton, confirmButton.dataset.loadingLabel || 'ADDING...');
  }

  if (form.requestSubmit) {
    setCardInlineQuickAddExpanded(root, false);
    form.requestSubmit(submitButton);
    window.setTimeout(() => {
      if (!confirmButton) return;
      confirmButton.classList.remove('loading', 'is-clicked');
      confirmButton.classList.add('is-added');
      setCardInlineQuickAddConfirmLabel(confirmButton, confirmButton.dataset.addedLabel || 'ADDED');
      if (!submitButton.disabled) {
        confirmButton.removeAttribute('aria-disabled');
      }
      window.setTimeout(() => {
        confirmButton.classList.remove('is-added');
        setCardInlineQuickAddConfirmLabel(confirmButton, confirmButton.dataset.defaultLabel || window.variantStrings?.addToCart || 'Add to cart');
      }, 1200);
    }, 520);
    return;
  }

  setCardInlineQuickAddExpanded(root, false);

  const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  const formData = new FormData(form);

  submitButton.setAttribute('aria-disabled', 'true');
  submitButton.classList.add('loading');
  root.querySelector('.loading__spinner')?.classList.remove('hidden');

  const config = fetchConfig('javascript');
  config.headers['X-Requested-With'] = 'XMLHttpRequest';
  delete config.headers['Content-Type'];

  if (cart && typeof cart.getSectionsToRender === 'function') {
    formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
    formData.append('sections_url', window.location.pathname);
    if (typeof cart.setActiveElement === 'function') {
      cart.setActiveElement(document.activeElement);
    }
  }

  config.body = formData;

  fetch(`${routes.cart_add_url}`, config)
    .then((response) => response.json())
    .then((response) => {
      if (response.status) {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'card-inline-quick-add',
          productVariantId: formData.get('id'),
          errors: response.errors || response.description,
          message: response.message,
        });

        const statusMessage = root.querySelector('[data-card-inline-quick-add-status]');
        if (statusMessage) {
          statusMessage.hidden = false;
          statusMessage.textContent = response.description || response.message || window.variantStrings?.soldOut || 'Sold out';
        }

        submitButton.setAttribute('disabled', 'disabled');
        return;
      }

      if (!cart) {
        window.location = window.routes.cart_url;
        return;
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'card-inline-quick-add',
          productVariantId: formData.get('id'),
          cartData: response,
        });
      }

      if (typeof cart.renderContents === 'function') {
        cart.renderContents(response);
      }

      if (confirmButton) {
        confirmButton.classList.add('is-added');
        setCardInlineQuickAddConfirmLabel(confirmButton, confirmButton.dataset.addedLabel || 'ADDED');
        window.setTimeout(() => {
          confirmButton.classList.remove('is-added');
          setCardInlineQuickAddConfirmLabel(confirmButton, confirmButton.dataset.defaultLabel || window.variantStrings?.addToCart || 'Add to cart');
        }, 1400);
      }
    })
    .catch((error) => {
      console.error('Card inline quick add failed', error);
    })
    .finally(() => {
      submitButton.classList.remove('loading');
      submitButton.removeAttribute('aria-disabled');
      root.querySelector('.loading__spinner')?.classList.add('hidden');

      if (confirmButton) {
        confirmButton.classList.remove('loading', 'is-clicked');
        if (!confirmButton.classList.contains('is-added')) {
          setCardInlineQuickAddConfirmLabel(confirmButton, confirmButton.dataset.defaultLabel || window.variantStrings?.addToCart || 'Add to cart');
        }
        if (!submitButton.disabled) {
          confirmButton.removeAttribute('disabled');
          confirmButton.removeAttribute('aria-disabled');
        }
      }
    });
}

function updateCardInlineQuickAddState(root) {
  const variants = getCardInlineQuickAddVariants(root);
  if (!variants.length) return;

  const selectedValues = getCardInlineQuickAddSelectedValues(root);
  updateCardInlineQuickAddSelectedLabels(root, selectedValues);
  const matchedVariant = getMatchedCardVariant(variants, selectedValues);

  const cardWrapper = root.closest('.card-wrapper');
  const variantInput = root.querySelector('.product-variant-id');
  const quantityInput = root.querySelector('.product-quantity-value');
  const submitButton = root.querySelector('.card-inline-quick-add__submit');
  const confirmButton = root.querySelector('[data-card-inline-quick-add-confirm]');
  const statusMessage = root.querySelector('[data-card-inline-quick-add-status]');
  const submitLabel = root.querySelector('[data-card-inline-quick-add-label]');
  const defaultLabel = submitButton?.dataset.defaultLabel || window.variantStrings?.addToCart || 'Add to cart';
  const currencyCodeEnabled = root.dataset.currencyCodeEnabled === 'true';

  if (!matchedVariant || !variantInput || !submitButton) {
    if (variantInput) variantInput.value = '';
    if (submitButton) submitButton.setAttribute('disabled', 'disabled');
    if (confirmButton) {
      confirmButton.setAttribute('disabled', 'disabled');
      confirmButton.setAttribute('aria-disabled', 'true');
    }
    if (statusMessage) statusMessage.hidden = false;
    syncCardInlineQuickAddSwatches(root);
    return;
  }

  variantInput.value = matchedVariant.id;
  variantInput.disabled = !matchedVariant.available;
  if (quantityInput) quantityInput.value = matchedVariant.quantity_rule_min || 1;

  if (cardWrapper) {
    updateCardInlineQuickAddPrice(cardWrapper, matchedVariant, currencyCodeEnabled);
    updateCardInlineQuickAddImage(cardWrapper, matchedVariant);
    commitCardProductCurrentImage(cardWrapper);
  }

  if (matchedVariant.available) {
    submitButton.removeAttribute('disabled');
    submitButton.removeAttribute('aria-disabled');
    if (confirmButton) {
      confirmButton.removeAttribute('disabled');
      confirmButton.removeAttribute('aria-disabled');
    }
    if (statusMessage) statusMessage.hidden = true;
    if (submitLabel) submitLabel.textContent = defaultLabel;
    if (confirmButton) setCardInlineQuickAddConfirmLabel(confirmButton, confirmButton.dataset.defaultLabel || defaultLabel);
  } else {
    submitButton.setAttribute('disabled', 'disabled');
    submitButton.setAttribute('aria-disabled', 'true');
    if (confirmButton) {
      confirmButton.setAttribute('disabled', 'disabled');
      confirmButton.setAttribute('aria-disabled', 'true');
    }
    if (statusMessage) statusMessage.hidden = false;
    if (submitLabel) submitLabel.textContent = window.variantStrings?.soldOut || 'Sold out';
    if (confirmButton) setCardInlineQuickAddConfirmLabel(confirmButton, window.variantStrings?.soldOut || 'Sold out');
  }

  getCardInlineQuickAddOptionGroups(root).forEach((group, groupIndex) => {
    group.querySelectorAll('.card-inline-quick-add__option-input').forEach((input) => {
      const testValues = [...selectedValues];
      testValues[groupIndex] = input.value;

      const hasMatch = variants.some(
        (variant) =>
          variant.available &&
          variant.options.every((optionValue, index) => optionValue === testValues[index])
      );

      input.disabled = !hasMatch;
      const chip = getOptionLabelForInput(root, input);
      if (chip) chip.classList.toggle('is-unavailable', !hasMatch);
    });
  });

  syncCardInlineQuickAddSwatches(root);
}

function initializeCardInlineQuickAdd(root) {
  if (!root || initializedCardInlineQuickAdds.has(root)) return;
  initializedCardInlineQuickAdds.add(root);
  setCardInlineQuickAddExpanded(root, false);
  updateCardInlineQuickAddState(root);
}

function initializeCardProductMedia(cardWrapper) {
  if (!cardWrapper) return;
  if (initializedCardProductMedia.has(cardWrapper)) return;
  initializedCardProductMedia.add(cardWrapper);

  syncCardProductMediaButtons(cardWrapper);
  if (!cardWrapper.dataset.cardProductMediaIndex) {
    cardWrapper.dataset.cardProductMediaIndex = '0';
  }
  if (!cardWrapper.dataset.cardProductCommittedMediaIndex) {
    cardWrapper.dataset.cardProductCommittedMediaIndex = cardWrapper.dataset.cardProductMediaIndex || '0';
  }
}

function previewCardProductSwatchImage(event) {
  const supportsHoverPreview = event.type === 'focusin' || supportsCardProductHoverPreview();

  if (!supportsHoverPreview) return;

  const swatchButton = event.target.closest('.card-swatch-button');
  if (!swatchButton || swatchButton.disabled || swatchButton.classList.contains('is-unavailable')) return;

  if (event.relatedTarget && swatchButton.contains(event.relatedTarget)) return;

  const cardWrapper = swatchButton.closest('.card-wrapper');
  if (!cardWrapper) return;

  cardWrapper.classList.add('is-card-swatch-previewing');
  updateCardProductImageFromSwatch(cardWrapper, swatchButton);
  commitCardProductCurrentImage(cardWrapper);
}

function previewCardInlineQuickAddSwatchImage(event) {
  const supportsHoverPreview = event.type === 'focusin' || supportsCardProductHoverPreview();

  if (!supportsHoverPreview) return;

  const chip = event.target.closest('.card-inline-quick-add__option-chip--swatch');
  if (!chip || chip.classList.contains('is-unavailable')) return;
  if (event.relatedTarget && chip.contains(event.relatedTarget)) return;

  const root = chip.closest('[data-card-inline-quick-add]');
  const cardWrapper = chip.closest('.card-wrapper');
  const input = chip.getAttribute('for') ? document.getElementById(chip.getAttribute('for')) : null;
  if (!root || !cardWrapper || !input || input.disabled) return;

  const groups = getCardInlineQuickAddOptionGroups(root);
  const group = input.closest('[data-card-inline-quick-add-option-position]');
  const groupIndex = groups.indexOf(group);
  if (groupIndex === -1) return;

  const previewValues = getCardInlineQuickAddSelectedValues(root);
  previewValues[groupIndex] = input.value;

  const previewVariant = getCardInlineQuickAddMatchedVariant(root, previewValues);
  if (!previewVariant?.featured_media?.src) return;

  cardWrapper.classList.add('is-card-swatch-previewing');
  updateCardInlineQuickAddImage(cardWrapper, previewVariant);
  commitCardProductCurrentImage(cardWrapper);
}

function restoreCardInlineQuickAddSwatchImage(event) {
  const chip = event.target.closest('.card-inline-quick-add__option-chip--swatch');
  if (!chip || (event.relatedTarget && chip.contains(event.relatedTarget))) return;

  const root = chip.closest('[data-card-inline-quick-add]');
  const cardWrapper = chip.closest('.card-wrapper');
  if (!root || !cardWrapper) return;

  if (event.relatedTarget && cardWrapper.contains(event.relatedTarget)) {
    commitCardProductCurrentImage(cardWrapper);
    return;
  }

  cardWrapper.classList.remove('is-card-swatch-previewing');
  commitCardProductCurrentImage(cardWrapper);
}

function restoreCardProductSwatchImage(event) {
  const swatchButton = event.target.closest('.card-swatch-button');
  if (!swatchButton || (event.relatedTarget && swatchButton.contains(event.relatedTarget))) return;

  const cardWrapper = swatchButton.closest('.card-wrapper');
  if (!cardWrapper) return;

  if (event.relatedTarget && cardWrapper.contains(event.relatedTarget)) {
    commitCardProductCurrentImage(cardWrapper);
    return;
  }

  cardWrapper.classList.remove('is-card-swatch-previewing');
  commitCardProductCurrentImage(cardWrapper);
}

function clearCardProductSwatchPreviewOnCardLeave(event) {
  const cardWrapper = event.target.closest?.('.card-wrapper.is-card-swatch-previewing');
  if (!cardWrapper || (event.relatedTarget && cardWrapper.contains(event.relatedTarget))) return;

  cardWrapper.classList.remove('is-card-swatch-previewing');
  commitCardProductCurrentImage(cardWrapper);
}

function getOpenCardQuickAddDrawer() {
  return document.querySelector('.card-quick-add-modal--drawer[open]');
}

function closeCardQuickAddDrawer(modal = getOpenCardQuickAddDrawer()) {
  if (!modal) return false;

  if (typeof modal.hide === 'function') {
    modal.hide();
  } else {
    modal.removeAttribute('open');
    document.body.classList.remove('overflow-hidden');
  }

  return true;
}

let cardQuickAddDrawerTouchStart = null;

function handleCardQuickAddDrawerTouchStart(event) {
  const modal = getOpenCardQuickAddDrawer();
  if (!modal || !modal.contains(event.target) || event.touches.length !== 1) return;

  const touch = event.touches[0];
  cardQuickAddDrawerTouchStart = {
    x: touch.clientX,
    y: touch.clientY,
    time: Date.now(),
  };
}

function handleCardQuickAddDrawerTouchEnd(event) {
  if (!cardQuickAddDrawerTouchStart) return;

  const modal = getOpenCardQuickAddDrawer();
  const touch = event.changedTouches?.[0];
  if (!modal || !touch) {
    cardQuickAddDrawerTouchStart = null;
    return;
  }

  const deltaX = touch.clientX - cardQuickAddDrawerTouchStart.x;
  const deltaY = touch.clientY - cardQuickAddDrawerTouchStart.y;
  const elapsed = Date.now() - cardQuickAddDrawerTouchStart.time;
  const isIntentionalSwipeRight = deltaX > 72 && Math.abs(deltaX) > Math.abs(deltaY) * 1.35 && elapsed < 700;

  cardQuickAddDrawerTouchStart = null;

  if (isIntentionalSwipeRight) {
    closeCardQuickAddDrawer(modal);
  }
}

document.addEventListener('focusin', previewCardProductSwatchImage);
document.addEventListener('focusin', previewCardInlineQuickAddSwatchImage);
document.addEventListener('focusout', restoreCardProductSwatchImage);
document.addEventListener('focusout', restoreCardInlineQuickAddSwatchImage);
document.addEventListener('focusout', clearCardProductSwatchPreviewOnCardLeave);

if (supportsCardProductHoverPreview()) {
  document.addEventListener('mouseover', previewCardProductSwatchImage);
  document.addEventListener('mouseover', previewCardInlineQuickAddSwatchImage);
  document.addEventListener('mouseout', restoreCardProductSwatchImage);
  document.addEventListener('mouseout', restoreCardInlineQuickAddSwatchImage);
  document.addEventListener('mouseout', clearCardProductSwatchPreviewOnCardLeave);
}

document.addEventListener('touchstart', handleCardQuickAddDrawerTouchStart, { passive: true });
document.addEventListener('touchend', handleCardQuickAddDrawerTouchEnd, { passive: true });

function openCardQuickAddModal(quickAddOpener, event) {
  const modal = quickAddOpener
    ? document.querySelector(quickAddOpener.getAttribute('data-card-quick-add-open'))
    : null;
  if (!modal) return false;

  const isHandledByModalOpener = Boolean(quickAddOpener.closest('modal-opener'));

  const track = modal.querySelector('.card-quick-add-modal__media-track');
  if (track) {
    track.scrollTo({ left: 0, top: 0, behavior: 'auto' });
    updateCardQuickAddDots(modal);
  }
  const content = modal.querySelector('.card-quick-add-modal__content');
  const details = modal.querySelector('.card-quick-add-modal__details');
  if (content) content.scrollTop = 0;
  if (details) details.scrollTop = 0;

  updateCardQuickAddState(modal);

  if (!isHandledByModalOpener) {
    event?.preventDefault();
    if (typeof modal.show === 'function') {
      modal.show(quickAddOpener);
    } else {
      modal.setAttribute('open', '');
      modal.removeAttribute('aria-hidden');
      document.body.classList.add('overflow-hidden');
    }
  }

  const resetQuickAddDrawerScroll = () => {
    if (content) content.scrollTop = 0;
    if (details) details.scrollTop = 0;
    if (track) {
      track.scrollTo({ left: 0, top: 0, behavior: 'auto' });
      updateCardQuickAddDots(modal);
    }
  };

  window.requestAnimationFrame(resetQuickAddDrawerScroll);
  window.setTimeout(resetQuickAddDrawerScroll, 80);
  window.setTimeout(resetQuickAddDrawerScroll, 240);

  return true;
}

function prefetchCardQuickAddModal(opener) {
  const productUrl = opener?.getAttribute?.('data-product-url');
  if (!productUrl || prefetchedCardQuickAddUrls.has(productUrl)) return;

  const modalSelector = opener.getAttribute('data-card-quick-add-open');
  const modal = modalSelector ? document.querySelector(modalSelector) : null;
  if (!modal || typeof modal.prefetchProductContent !== 'function') return;

  prefetchedCardQuickAddUrls.add(productUrl);
  modal.prefetchProductContent(productUrl).catch(() => {
    prefetchedCardQuickAddUrls.delete(productUrl);
  });
}

function loadCardHoverImage(cardWrapper) {
  const template = cardWrapper?.querySelector('template[data-card-hover-image]');
  if (!template || template.dataset.loading) return;
  const image = template.content.querySelector('img')?.cloneNode(true);
  if (!image) return;
  template.dataset.loading = 'true';
  image.loading = 'eager';
  const markLoaded = () => {
    if (!image.naturalWidth) return;
    cardWrapper.classList.add('has-loaded-hover-image');
    template.remove();
  };
  image.addEventListener('load', markLoaded, { once: true });
  image.addEventListener('error', () => {
    image.remove();
    delete template.dataset.loading;
  }, { once: true });
  template.before(image);
  if (image.complete) markLoaded();
}

document.addEventListener('click', (event) => {
  const inlineQuickAddToggle = event.target.closest('[data-card-inline-quick-add-toggle]');
  if (inlineQuickAddToggle) {
    const root = inlineQuickAddToggle.closest('[data-card-inline-quick-add]');
    if (!root) return;
    initializeCardInlineQuickAdd(root);

    const nextExpandedState = !root.classList.contains('is-expanded');
    closeAllCardInlineQuickAdds(root);
    setCardInlineQuickAddExpanded(root, nextExpandedState);
    return;
  }

  const inlineQuickAddClose = event.target.closest('[data-card-inline-quick-add-close]');
  if (inlineQuickAddClose) {
    const root = inlineQuickAddClose.closest('[data-card-inline-quick-add]');
    if (!root) return;
    initializeCardInlineQuickAdd(root);

    setCardInlineQuickAddExpanded(root, false);
    return;
  }

  const inlineQuickAddChip = event.target.closest('[data-card-inline-quick-add-trigger-submit="true"]');
  if (inlineQuickAddChip) {
    const root = inlineQuickAddChip.closest('[data-card-inline-quick-add]');
    const input = document.getElementById(inlineQuickAddChip.getAttribute('for'));
    if (!root || !input || input.disabled) return;
    initializeCardInlineQuickAdd(root);

    event.preventDefault();
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    updateCardInlineQuickAddState(root);
    submitCardInlineQuickAdd(root);
    return;
  }

  const inlineQuickAddOptionChip = event.target.closest('.card-inline-quick-add__option-chip');
  if (inlineQuickAddOptionChip) {
    const root = inlineQuickAddOptionChip.closest('[data-card-inline-quick-add]');
    const input = document.getElementById(inlineQuickAddOptionChip.getAttribute('for'));
    if (!root || !input || input.disabled) return;
    initializeCardInlineQuickAdd(root);

    event.preventDefault();
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    updateCardInlineQuickAddState(root);
    if (inlineQuickAddOptionChip.classList.contains('card-inline-quick-add__option-chip--swatch')) {
      const cardWrapper = inlineQuickAddOptionChip.closest('.card-wrapper');
      cardWrapper?.classList.add('is-card-swatch-previewing');
      commitCardProductCurrentImage(cardWrapper);
    }
    return;
  }

  const inlineQuickAddConfirm = event.target.closest('[data-card-inline-quick-add-confirm]');
  if (inlineQuickAddConfirm) {
    const root = inlineQuickAddConfirm.closest('[data-card-inline-quick-add]');
    if (!root || inlineQuickAddConfirm.disabled || inlineQuickAddConfirm.getAttribute('aria-disabled') === 'true') return;
    initializeCardInlineQuickAdd(root);

    event.preventDefault();
    submitCardInlineQuickAdd(root);
    return;
  }

  const swatchButton = event.target.closest('.card-swatch-button');
  if (swatchButton) {
    const cardWrapper = swatchButton.closest('.card-wrapper');
    if (!cardWrapper) return;

    const inlineQuickAddRoot = cardWrapper.querySelector('[data-card-inline-quick-add]');
    if (inlineQuickAddRoot && swatchButton.dataset.cardSwatchOptionPosition) {
      if (swatchButton.disabled || swatchButton.classList.contains('is-unavailable')) return;
      initializeCardInlineQuickAdd(inlineQuickAddRoot);

      const input = findCardInlineQuickAddOptionInput(
        inlineQuickAddRoot,
        swatchButton.dataset.cardSwatchOptionPosition,
        swatchButton.dataset.cardSwatchValue || swatchButton.dataset.value || ''
      );
      if (!input || input.disabled) return;

      input.checked = true;
      setActiveCardSwatch(cardWrapper, swatchButton);
      input.dispatchEvent(new Event('change', { bubbles: true }));
      updateCardInlineQuickAddState(inlineQuickAddRoot);
      cardWrapper.classList.remove('is-card-swatch-previewing');
      commitCardProductCurrentImage(cardWrapper);
      return;
    }

    const variantId = swatchButton.dataset.variantId;
    if (!variantId) return;

    setActiveCardSwatch(cardWrapper, swatchButton);

    cardWrapper.querySelectorAll('.product-variant-id').forEach((input) => {
      input.value = variantId;
      input.disabled = false;
    });

    updateCardProductImageFromSwatch(cardWrapper, swatchButton);
    commitCardProductCurrentImage(cardWrapper);
    return;
  }

  const mediaNavButton = event.target.closest('[data-card-product-media-nav]');
  if (mediaNavButton) {
    const cardWrapper = mediaNavButton.closest('.card-wrapper');
    if (!cardWrapper) return;
    initializeCardProductMedia(cardWrapper);

    event.preventDefault();
    event.stopPropagation();

    const direction = mediaNavButton.dataset.cardProductMediaNav;
    const currentIndex = getCardProductMediaIndex(cardWrapper);
    const nextIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;
    setCardProductMediaByIndex(cardWrapper, nextIndex);
    return;
  }

  const quickAddOpener = event.target.closest('[data-card-quick-add-open]');
  if (quickAddOpener) {
    openCardQuickAddModal(quickAddOpener, event);
    return;
  }

  const dotButton = event.target.closest('[data-card-quick-add-media-dot]');
  if (dotButton) {
    const modal = dotButton.closest('.card-quick-add-modal');
    if (!modal) return;
    scrollCardQuickAddMediaToIndex(modal, Number(dotButton.dataset.cardQuickAddMediaIndex || 0));
    return;
  }

  const modalMediaNavButton = event.target.closest('[data-card-quick-add-media-nav]');
  if (modalMediaNavButton) {
    const modal = modalMediaNavButton.closest('.card-quick-add-modal');
    if (!modal) return;

    event.preventDefault();
    scrollCardQuickAddMediaByDirection(modal, modalMediaNavButton.dataset.cardQuickAddMediaNav || 'next');
    return;
  }

  if (!event.target.closest('[data-card-inline-quick-add]')) {
    closeAllCardInlineQuickAdds();
  }
});

document.addEventListener('change', (event) => {
  const inlineOptionInput = event.target.closest('.card-inline-quick-add__option-input');
  if (inlineOptionInput) {
    const root = inlineOptionInput.closest('[data-card-inline-quick-add]');
    if (!root) return;
    initializeCardInlineQuickAdd(root);

    updateCardInlineQuickAddState(root);
    return;
  }

  const optionInput = event.target.closest('.card-quick-add-modal__option-input');
  if (!optionInput) return;

  const modal = optionInput.closest('.card-quick-add-modal');
  if (!modal) return;

  updateCardQuickAddState(modal);
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  closeCardQuickAddDrawer();
  closeAllCardInlineQuickAdds();
});

document.addEventListener(
  'pointerenter',
  (event) => {
    if (event.pointerType !== 'touch' && supportsCardProductHoverPreview()) {
      loadCardHoverImage(event.target.closest?.('.product-card-wrapper'));
    }
    const quickAddOpener = event.target.closest?.('[data-card-quick-add-open]');
    if (!quickAddOpener || event.pointerType === 'touch') return;
    prefetchCardQuickAddModal(quickAddOpener);
  },
  { capture: true, passive: true }
);

document.addEventListener(
  'focusin',
  (event) => {
    if (supportsCardProductHoverPreview()) loadCardHoverImage(event.target.closest?.('.product-card-wrapper'));
    const quickAddOpener = event.target.closest?.('[data-card-quick-add-open]');
    if (!quickAddOpener) return;
    prefetchCardQuickAddModal(quickAddOpener);
  },
  { capture: true, passive: true }
);

document.addEventListener(
  'touchstart',
  (event) => {
    const quickAddOpener = event.target.closest?.('[data-card-quick-add-open]');
    if (!quickAddOpener) return;
    prefetchCardQuickAddModal(quickAddOpener);
  },
  { capture: true, passive: true }
);

document.addEventListener(
  'scroll',
  (event) => {
    const track = event.target.closest?.('.card-quick-add-modal__media-track');
    if (!track) return;

    const modal = track.closest('.card-quick-add-modal');
    if (!modal) return;

    updateCardQuickAddDots(modal);
  },
  { capture: true, passive: true }
);

function observeCardInlineQuickAdd(root) {
  if (!root || initializedCardInlineQuickAdds.has(root)) return;

  if (!('IntersectionObserver' in window)) {
    initializeCardInlineQuickAdd(root);
    return;
  }

  if (!cardInlineQuickAddObserver) {
    cardInlineQuickAddObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          initializeCardInlineQuickAdd(entry.target);
          cardInlineQuickAddObserver.unobserve(entry.target);
        });
      },
      { rootMargin: getCardProductObserverRootMargin('inline') }
    );
  }

  cardInlineQuickAddObserver.observe(root);
}

function observeCardProductMedia(cardWrapper) {
  if (!cardWrapper || initializedCardProductMedia.has(cardWrapper)) return;

  if (!('IntersectionObserver' in window)) {
    initializeCardProductMedia(cardWrapper);
    return;
  }

  if (!cardProductMediaObserver) {
    cardProductMediaObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          initializeCardProductMedia(entry.target);
          cardProductMediaObserver.unobserve(entry.target);
        });
      },
      { rootMargin: getCardProductObserverRootMargin('media') }
    );
  }

  cardProductMediaObserver.observe(cardWrapper);
}

function hydrateProductCards(container = document) {
  container.querySelectorAll('[data-card-inline-quick-add]').forEach(observeCardInlineQuickAdd);
  container.querySelectorAll('.card-wrapper.product-card-wrapper').forEach(observeCardProductMedia);
}

hydrateProductCards();

document.addEventListener('product-grid:updated', () => {
  hydrateProductCards();
});
